/**
 * Social post templates — structured frameworks for common content types.
 * Each template injects a framework prompt into the AI system message.
 */
export const SOCIAL_TEMPLATES = [
  {
    key: 'freeform',
    name: 'Freeform',
    category: 'general',
    desc: 'Write anything — no structure imposed',
    prompt: ''
  },
  {
    key: 'product_launch',
    name: 'Product Launch',
    category: 'marketing',
    desc: 'Announce a new product or feature',
    prompt: 'Structure this as a product launch announcement: Hook with the problem it solves → Introduce the product/feature → 2-3 key benefits → Clear CTA to try/buy. Make it feel exciting but not salesy.'
  },
  {
    key: 'behind_the_scenes',
    name: 'Behind the Scenes',
    category: 'engagement',
    desc: 'Show the human side of your brand',
    prompt: 'Structure this as a behind-the-scenes peek: Open with a candid moment → Share the context/story → Connect it to a broader lesson or value → End with a question to invite engagement. Tone should feel authentic and unpolished.'
  },
  {
    key: 'testimonial_spotlight',
    name: 'Testimonial Spotlight',
    category: 'social_proof',
    desc: 'Share a customer success story',
    prompt: 'Structure this as a testimonial spotlight: Start with the customer problem → Describe the transformation → Include a realistic quote or paraphrase → End with a CTA. Make it feel genuine, not scripted.'
  },
  {
    key: 'poll_question',
    name: 'Poll / Question',
    category: 'engagement',
    desc: 'Ask your audience a question',
    prompt: 'Structure this as an engagement post: Open with a provocative or relatable question → Provide 2-4 options or perspectives → Encourage people to comment with their answer → Keep it short and punchy. The goal is maximum comments.'
  },
  {
    key: 'educational_tip',
    name: 'Educational Tip',
    category: 'value',
    desc: 'Teach something valuable in one post',
    prompt: 'Structure this as an educational micro-lesson: Start with "Most people get [X] wrong" or "Here\'s what I wish I knew about [X]" → Share 1 actionable tip with a specific example → End with a takeaway they can use today. Make it feel like insider knowledge.'
  },
  {
    key: 'thought_leadership',
    name: 'Thought Leadership',
    category: 'authority',
    desc: 'Share an industry insight or hot take',
    prompt: 'Structure this as thought leadership: Open with a bold observation or contrarian take → Support it with evidence or experience → Acknowledge the counterargument briefly → End with a forward-looking statement. Write for peers, not beginners.'
  },
  {
    key: 'story_arc',
    name: 'Story / Narrative',
    category: 'engagement',
    desc: 'Tell a compelling story',
    prompt: 'Structure this as a story arc: Set the scene (time, place, characters) → Introduce the conflict or challenge → Describe the turning point → Reveal the resolution or lesson → End with a universal takeaway. Use short paragraphs and line breaks for readability.'
  },
  {
    key: 'engagement_hook',
    name: 'Engagement Hook',
    category: 'engagement',
    desc: 'Stop the scroll and start a conversation',
    prompt: 'Structure this as a scroll-stopping hook: Open with an unexpected statement, stat, or confession → Build curiosity in 2-3 sentences → Deliver the insight or punchline → End with "What do you think?" or "Share your experience." Optimize for saves and shares.'
  },
  {
    key: 'milestone',
    name: 'Milestone / Celebration',
    category: 'brand',
    desc: 'Celebrate an achievement',
    prompt: 'Structure this as a milestone celebration: Start with the achievement (be specific with numbers) → Credit the team/community → Share what you learned along the way → Look forward to what\'s next → Thank the audience. Be grateful without being cheesy.'
  },
  {
    key: 'user_spotlight',
    name: 'User / Community Spotlight',
    category: 'social_proof',
    desc: 'Highlight a member of your community',
    prompt: 'Structure this as a community spotlight: Introduce the person and what they do → Describe their journey or achievement → Connect it to your brand\'s mission → Tag them and encourage others to share their stories. Make THEM the hero, not your brand.'
  },
  {
    key: 'controversy_take',
    name: 'Contrarian / Hot Take',
    category: 'authority',
    desc: 'Challenge conventional wisdom',
    prompt: 'Structure this as a contrarian take: State the popular belief → Explain why it\'s wrong or incomplete → Share your alternative perspective with evidence → End with an invitation for debate. Be confident but not dismissive. The goal is thoughtful disagreement, not rage bait.'
  },
  {
    key: 'seasonal_timely',
    name: 'Seasonal / Timely',
    category: 'marketing',
    desc: 'Tie into a season, holiday, or trend',
    prompt: 'Structure this as a timely post: Reference the season, holiday, or current event → Connect it naturally to your brand or industry → Provide value (not just "Happy [Holiday]!") → Include a seasonal CTA. Make the connection feel organic, not forced.'
  },
  {
    key: 'faq_myth',
    name: 'FAQ / Myth Buster',
    category: 'value',
    desc: 'Answer a common question or bust a myth',
    prompt: 'Structure this as a myth-buster: State the common belief or question → Explain why it\'s wrong or incomplete → Share the real answer with a quick proof point → End with "Have a question? Drop it below." Position yourself as the go-to expert.'
  },
  {
    key: 'carousel_thread',
    name: 'Thread / Carousel',
    category: 'value',
    desc: 'Multi-part educational content',
    prompt: 'Structure this as a thread or carousel series. Create 5-7 connected points, each as its own short paragraph separated by a blank line. Start with a hook slide ("Here\'s what nobody tells you about [X]:") → Build through numbered points → End with a summary takeaway and CTA. Each point should be self-contained but build on the last.'
  },
  {
    key: 'comparison',
    name: 'Comparison / VS',
    category: 'value',
    desc: 'Compare two approaches, tools, or ideas',
    prompt: 'Structure this as a comparison: Set up the two options clearly → List 2-3 key differences → Give your recommendation and why → End with "Which do you prefer?" Make it balanced but have a clear opinion.'
  }
];

export const SOCIAL_TEMPLATE_CATEGORIES = [
  { key: 'general', label: 'All' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'value', label: 'Value' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'authority', label: 'Authority' },
  { key: 'social_proof', label: 'Social Proof' },
  { key: 'brand', label: 'Brand' }
];
